import {
  Button,
  Grid,
  IconButton,
  Tooltip,
  Typography
} from '@mui/material'
import React, { Fragment, useEffect, useState } from 'react'
import { Status } from '../../hooks/useFetchState'
import { GenericTable, TableHeaderConfigWithType } from '../_EOS/Table'
import { useApi } from '../../hooks/useApi'
import { ApiResponse } from '../../typings/ApiResponse'
import { SearchReturnFilters } from './SearchReturnFilters'
import { getAsnReturn, deletePreAsn, softDeleteAsn, cancelAsn } from './api'
import { Filter } from '../_WMS/FilterComponent'
import { usePagination } from '../../hooks/usePagination'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import DeleteIcon from '@mui/icons-material/Delete'
import { eAsnState } from '../../enums/eAsnState'
import { useModal } from '../../hooks/useModal'
import { Modal } from '../_EOS/Modal'

export interface AsnReturnDto {
  asnId?: number | null
  preInboundId?: number | null
  asnNumber: string
  trackingNumber: string
  typeReturn: string
  workOrderId?: number | null
  externalOrderId: string
  courier: string
  typeInboundReturn: string
  orderId: number
  asnStateId: number
  typeAsnIncomeId: number
  warehouseId: number
}

export interface SearchOrderProps {
  handleStep: React.Dispatch<React.SetStateAction<number>>
  handleSnackbar: (msg: string | undefined, status: boolean) => void
  setSelectedOrderId: React.Dispatch<React.SetStateAction<number>>
  setSelectedAsnId: React.Dispatch<React.SetStateAction<number>>
  setSelectedAsnNumber: React.Dispatch<React.SetStateAction<string>>
  setSelectedPreinboundId: React.Dispatch<React.SetStateAction<number>>
  setNewPreInbound: React.Dispatch<React.SetStateAction<boolean>>
  setSelectedAsnReturnDto: React.Dispatch<
    React.SetStateAction<AsnReturnDto | undefined>
  >
}

type CustomActionsProps = {
  id: number
  row: AsnReturnDto
}

const tableHeader: TableHeaderConfigWithType<AsnReturnDto>[] = [
  {
    field: 'asnNumber',
    title: 'Número Asn',
    sortable: false
  },
  {
    field: 'workOrderId',
    title: 'OT',
    sortable: false
  },
  {
    field: 'externalOrderId',
    title: 'ID Externo',
    sortable: false
  },
  {
    field: 'trackingNumber',
    title: 'Tracking Number',
    sortable: false
  },
  {
    field: 'courier',
    title: 'Courier',
    sortable: false
  },
  {
    field: 'typeInboundReturn',
    title: 'Tipo',
    sortable: false
  }
]

export const SearchReturn: React.FC<SearchOrderProps> = ({
  handleStep,
  setSelectedOrderId,
  setSelectedAsnId,
  setSelectedAsnNumber,
  setNewPreInbound,
  setSelectedAsnReturnDto,
  setSelectedPreinboundId
}) => {
  const confirmModal = useModal<AsnReturnDto>('Confirmar')
  const { state: stateAsnReturn, callApi } = useApi<ApiResponse<AsnReturnDto>>()
  const { callApi: callApiDelete } = useApi()
  const pagination = usePagination(5)
  const [selectedFilters, setSelectedFilters] = React.useState<Filter[]>([])
  const [loading, setLoading] = useState(false)

  const requestAsnReturn = (
    filters?: Filter[],
    page?: number,
    pageSize?: number
  ) => {
    callApi(getAsnReturn(filters, page, pageSize)).catch((error) => {
      console.log(error)
    })
  }

  const handlePaginationChange = (page: number, pageSize: number) => {
    // If the pageSize is different, we should set page 0
    const auxPage = pagination.pageSize !== pageSize ? 0 : page

    requestAsnReturn(selectedFilters, auxPage, pageSize)

    pagination.setPagination(auxPage, pageSize)
  }

  useEffect(() => {
    pagination.setPagination(0, pagination.pageSize)

    requestAsnReturn(selectedFilters, 0, pagination.pageSize)
  }, [selectedFilters])

  // si es pre-asn o asn llama su delete correspondiente.
  const handleDeteAsnByType = (row: AsnReturnDto | undefined) => {
    if (row === undefined) return

    setLoading(true)
    // Si pre-preInboundId > 0 es una devolución normal
    if (row?.preInboundId && row.preInboundId > 0) {
      callApiDelete(deletePreAsn(row?.preInboundId))
        .then(() => requestAsnReturn(selectedFilters, 0, pagination.pageSize))
        .finally(() => {
          setLoading(false)
          confirmModal.closeModal()
        })
    } else {
      if (eAsnState.Pending === row.asnStateId) {
        callApiDelete(softDeleteAsn(row.asnId || 0))
          .then(() => requestAsnReturn(selectedFilters, 0, pagination.pageSize))
          .finally(() => {
            setLoading(false)
            confirmModal.closeModal()
          })
      }

      if (eAsnState.Received === row.asnStateId) {
        callApiDelete(cancelAsn(row.asnId || 0))
          .then(() => requestAsnReturn(selectedFilters, 0, pagination.pageSize))
          .finally(() => {
            setLoading(false)
            confirmModal.closeModal()
          })
      }
    }
  }

  // En caso de que esté la pre-asn creada. Continua con la selección de lote
  // En caso de que esté la Asn creada. debe ir al formulario de calidad
  const handleContinueReturnSelectionAndQuality = (row: AsnReturnDto) => {
    // Si pre-preInboundId > 0 es una devolución normal
    if (row.preInboundId && row?.preInboundId > 0) {
      setNewPreInbound(false)
      setSelectedOrderId(row.orderId)
      setSelectedPreinboundId(row.preInboundId)
      handleStep(4)
      setSelectedAsnReturnDto(row) // guarda los datos de pre asn para llenar dto para cresar asn en formulario de seleccion de lote (step 4)
    } else {
      setSelectedAsnReturnDto(row)
      setSelectedAsnId(row.asnId || 0)
      setSelectedAsnNumber(row.asnNumber || '')
      setNewPreInbound(false)
      setSelectedOrderId(row.orderId)
      handleStep(5)
    }
  }

  const MyCustomActions: React.FC<CustomActionsProps> = ({ row }) => {
    return (
      <Fragment>
        <Tooltip title='Aceptar' aria-label='edit'>
          <IconButton
            aria-label='seeBinacle'
            onClick={() => handleContinueReturnSelectionAndQuality(row)}
            style={{ verticalAlign: 'bottom' }}
          >
            <CheckCircleIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title='Eliminar' aria-label='edit'>
          <IconButton
            aria-label='seeBinacle'
            onClick={() => confirmModal.openModal('xs', row)}
            style={{ verticalAlign: 'bottom' }}
          >
            <DeleteIcon />
          </IconButton>
        </Tooltip>
      </Fragment>
    )
  }

  return (
    <Fragment>
      <Grid container spacing={2}>
        <Grid item xs={12} md={8}>
          <SearchReturnFilters
            selectedFilters={selectedFilters}
            setSelectedFilters={setSelectedFilters}
            onSearchClick={requestAsnReturn}
            defaultSelectedFilterKey='trackingNumber'
          />
        </Grid>
        <Grid item md={2}></Grid>
        <Grid item xs={12} md={2} style={{ paddingTop: '17px' }}>
          <Button
            fullWidth
            type='button'
            variant='contained'
            color='primary'
            onClick={() => handleStep(1)}
          >
            CREAR DEVOLUCIÓN
          </Button>
        </Grid>
        <Grid item xs={12} md={12} alignContent='center' alignItems='center'>
          <GenericTable<AsnReturnDto>
            dense={true}
            headersConfig={tableHeader || []}
            IDKey={'asnId'}
            data={stateAsnReturn.data?.items}
            pagination={{
              totalItem: stateAsnReturn.data?.totalItems || 0,
              page: pagination.page,
              pageSize: pagination.pageSize
            }}
            onPaginationChange={handlePaginationChange}
            isLoading={stateAsnReturn.status === Status.Loading}
            showCheckboxes={false}
            customActionComponent={MyCustomActions}
          />
        </Grid>
      </Grid>
      <Modal
        loading={loading}
        open={confirmModal.isOpen}
        title={confirmModal.title}
        onClose={confirmModal.closeModal}
        cancelButton={{
          action: confirmModal.closeModal,
          title: 'Cancelar'
        }}
        successButton={{
          action: () => handleDeteAsnByType(confirmModal.extendedProps),
          title: 'Guardar'
        }}
      >
        <Typography variant='h6'>¿Está seguro que desea eliminar ?</Typography>
      </Modal>
    </Fragment>
  )
}
